import { useApolloClient } from "@apollo/client";
import React from "react";
import type { TaxonomyIndex } from "../offeredServices/catalogueModel.js";
import type { OnboardingFlowStage } from "./OnboardingFlowModal.js";
import { useOnboardingFlowModal_SetOrganisationOfferedServicesMutation } from "./OnboardingFlowModal.generated.js";
import { selectOfferedServicesPayload } from "./onboardingFlowWizardReducer.js";
import type {
  OfferedServicesCommitPath,
  OnboardingFlowWizardState,
} from "./onboardingFlowWizardState.js";

export type UseSetOrganisationOfferedServices = {
  commit: (path: OfferedServicesCommitPath) => Promise<void>;
  error: Error | null;
  loading: boolean;
};

export function useSetOrganisationOfferedServices(
  state: OnboardingFlowWizardState,
  setStage: (newStage: OnboardingFlowStage | null) => void,
  index: TaxonomyIndex
): UseSetOrganisationOfferedServices {
  const client = useApolloClient();
  const [mutate, { loading }] =
    useOnboardingFlowModal_SetOrganisationOfferedServicesMutation({ client });
  const [error, setError] = React.useState<Error | null>(null);
  // Re-entrancy guard: if a CONTINUE click fires while a previous commit is
  // still in flight, the second call is dropped. Apollo's `loading` flag
  // covers the synchronous case; this guard covers the
  // promise-already-resolved-but-state-not-flushed window.
  const inFlight = React.useRef(false);

  const commit = React.useCallback(
    async (path: OfferedServicesCommitPath) => {
      if (inFlight.current) return;
      inFlight.current = true;
      setError(null);
      try {
        const payload = selectOfferedServicesPayload(state, path, index);
        await mutate({
          variables: { input: payload },
        });
        setStage("success");
      } catch (e) {
        setError(e instanceof Error ? e : new Error(String(e)));
      } finally {
        inFlight.current = false;
      }
    },
    [mutate, state, setStage, index]
  );

  return { commit, error, loading };
}
